import axios from "@/api/axios";

export interface FieldValue {
  booleanValue?: boolean;
  dateValue?: string;
  fieldId: string;
  id: string;
  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  jsonValue?: any;
  numberValue?: number;
  stringValue?: string;
  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  value: any;
}

export interface StandardField {
  category?: string;
  columnName?: string;
  dataType: "BOOLEAN" | "DATE" | "JSON" | "NUMBER" | "STRING";
  displayName: string;
  displayOrder: number;
  entityType: "CASE" | "CLIENT";
  fieldKey: string;
  format?: string;
  id: string;
  isDefault: boolean;
  isHardCoded: boolean;
  isPII: boolean;
}

export interface FieldWithValue extends StandardField {
  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  value: any;
}

export interface GetFieldValuesResponse {
  campaignId: string;
  entityId: string;
  entityType: string;
  fields: FieldWithValue[];
}

export interface SetFieldValueRequest {
  campaignId: string;
  entityId: string;
  entityType: "CASE" | "CLIENT";
  fieldId: string;
  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  value: any;
}

export interface BulkSetFieldValuesRequest {
  campaignId: string;
  entityId: string;
  entityType: "CASE" | "CLIENT";
  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  fieldValues: Record<string, any>;
}

export interface SyncDefaultFieldsRequest {
  campaignId?: string;
  dryRun?: boolean;
}

export interface SyncDefaultFieldsResponse {
  changes?: Array<{
    action: string;
    campaignId: string;
    campaignName: string;
    fieldKey: string;
    fieldName: string;
  }>;
  message: string;
  stats: {
    campaignsProcessed: number;
    fieldsAdded: number;
    fieldsRemoved: number;
    fieldsSkipped: number;
  };
  success: boolean;
}

export interface DefaultFieldsStatusResponse {
  campaigns: Array<{
    campaignId: string;
    campaignName: string;
    hasAllDefaults: boolean;
    mappedDefaultFields: number;
    missingDefaultFields: number;
    missingFields: Array<{
      displayName: string;
      entityType: string;
      fieldKey: string;
    }>;
    totalDefaultFields: number;
  }>;
  defaultFields: Array<{
    displayName: string;
    entityType: string;
    fieldKey: string;
  }>;
  success: boolean;
  summary: {
    campaignsNeedingSync: number;
    campaignsWithAllDefaults: number;
    totalCampaigns: number;
    totalDefaultFields: number;
  };
}

export interface ViewsSyncStatusResponse {
  defaultFields: Array<{
    displayName: string;
    entityType: string;
    fieldId: string;
    fieldKey: string;
  }>;
  success: boolean;
  summary: {
    totalCampaigns: number;
    totalDefaultFields: number;
    totalViews: number;
    viewsNeedingSync: number;
    viewsWithAllDefaults: number;
  };
  views: Array<{
    appliesToRole: null | string;
    campaignId: string;
    campaignName: string;
    currentFieldCount: number;
    hasAllDefaults: boolean;
    isDefault: boolean;
    missingDefaultFields: number;
    missingFields: Array<{
      displayName: string;
      entityType: string;
      fieldId: string;
      fieldKey: string;
    }>;
    viewId: string;
    viewName: string;
  }>;
}

export interface SyncToViewsRequest {
  dryRun?: boolean;
  viewIds: string[];
}

export interface SyncToViewsResponse {
  changes?: Array<{
    action: string;
    campaignId: string;
    campaignName: string;
    entityType: string;
    fieldId: string;
    fieldKey: string;
    fieldName: string;
    viewId: string;
    viewName: string;
  }>;
  message: string;
  stats: {
    fieldsAdded: number;
    fieldsSkipped: number;
    viewsProcessed: number;
  };
  success: boolean;
}

class FieldValueService {
  /**
   * Get field values for an entity
   */
  async getFieldValues(
    entityType: "CASE" | "CLIENT",
    entityId: string,
    campaignId: string
  ): Promise<GetFieldValuesResponse> {
    const response = await axios.get<GetFieldValuesResponse>("/field-values", {
      params: { campaignId, entityId, entityType },
    });
    return response.data;
  }

  /**
   * Set a single field value
   */
  async setFieldValue(data: SetFieldValueRequest): Promise<void> {
    await axios.post("/field-values", data);
  }

  /**
   * Bulk set field values
   */
  async bulkSetFieldValues(data: BulkSetFieldValuesRequest): Promise<void> {
    await axios.put("/field-values/bulk", data);
  }

  /**
   * Search entities by field value
   */
  async searchByFieldValue(
    fieldKey: string,
    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    value: any,
    campaignId: string,
    options?: {
      entityType?: "CASE" | "CLIENT";
      limit?: number;
      offset?: number;
    } // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ): Promise<any> {
    const response = await axios.get("/field-values/search", {
      params: {
        campaignId,
        fieldKey,
        value,
        ...options,
      },
    });
    return response.data;
  }

  /**
   * Get campaign fields with values for a specific entity
   */
  async getCampaignFieldsWithValues(
    campaignId: string,
    entityType: "CASE" | "CLIENT",
    entityId: string
    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ): Promise<any> {
    const response = await axios.get("/campaigns/fields-with-values", {
      params: { campaignId, entityId, entityType },
    });
    return response.data;
  }

  /**
   * Delete a field value
   */
  async deleteFieldValue(
    fieldId: string,
    entityType: "CASE" | "CLIENT",
    entityId: string
  ): Promise<void> {
    await axios.delete(`/field-values/${fieldId}`, {
      params: { entityId, entityType },
    });
  }

  /**
   * Get field statistics
   */
  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  async getFieldStatistics(campaignId: string, fieldId: string): Promise<any> {
    const response = await axios.get(`/campaigns/${campaignId}/fields/${fieldId}/statistics`);
    return response.data;
  }

  /**
   * Copy field values between entities
   */
  async copyFieldValues(
    sourceEntityId: string,
    targetEntityId: string,
    entityType: "CASE" | "CLIENT",
    campaignId: string
  ): Promise<void> {
    await axios.post("/field-values/copy", {
      campaignId,
      entityType,
      sourceEntityId,
      targetEntityId,
    });
  }

  /**
   * Sync default fields to campaigns
   */
  async syncDefaultFields(data: SyncDefaultFieldsRequest): Promise<SyncDefaultFieldsResponse> {
    const response = await axios.post<SyncDefaultFieldsResponse>(
      "/admin/fields/sync-default-fields",
      data
    );
    return response.data;
  }

  /**
   * Get default fields sync status
   */
  async getDefaultFieldsStatus(): Promise<DefaultFieldsStatusResponse> {
    const response = await axios.get<DefaultFieldsStatusResponse>(
      "/admin/fields/default-fields-status"
    );
    return response.data;
  }

  /**
   * Get views sync status - shows which views need default fields
   */
  async getViewsSyncStatus(): Promise<ViewsSyncStatusResponse> {
    const response = await axios.get<ViewsSyncStatusResponse>("/admin/fields/views-sync-status");
    return response.data;
  }

  /**
   * Sync default fields to selected views (Step 2 of DefaultFieldSync)
   */
  async syncToViews(data: SyncToViewsRequest): Promise<SyncToViewsResponse> {
    const response = await axios.post<SyncToViewsResponse>("/admin/fields/sync-to-views", data);
    return response.data;
  }
}

export const fieldValueService = new FieldValueService();
export default fieldValueService;
